import { ReactNode, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { post, patch } from "services/orders.service";
import { AppDispatch, RootState } from "configureStore";
import DatePicker from "features/datepicker/date-picker";
import { isContractTermIncomplete } from "features/billing-period/contract-term-field";
import { vilniusYmd } from "utils/vilnius-date";
import ProductsSelect from "components/products-select/ProductsSelect";
import ProductGroupSelect from "components/products-select/ProductGroupSelect";
import UserSelect from "features/user-select/user-select";
import { Product } from "types/Product";
import { fetchCustomerOrdersList } from "features/customers-orders/customer-orders.slice";
import CustomerSelect from "components/customer-select/CustomerSelect";
import Loading from "components/loading/Loading";
import { fetchOrders } from "features/orders/orders.slice";
import { fetchOrderDetails } from "containers/order-view-container/order-container.slice";
import DraftBanner from "./draft-banner";
import ExternalContractFields from "./external-contract-fields";
import InvoicesSection, { isScheduleDayInvalid } from "./invoices-section";
import { buildOrderPayload } from "./build-order-payload";
import { OrderStart, newOrderStart, useLoadedOrder } from "./use-loaded-order";
import { orderDraftKey, useOrderDraft } from "./use-order-draft";
import Notice from "components/tw/notice";
import {
  btnIconCls,
  btnPrimaryCls,
  btnSecondaryCls,
  cardCls,
  inputCls,
  labelCls,
} from "components/tw/classes";
import { externalContractErrorMessage } from "utils/external-contract-errors";
import { canManageSalesInvoiceSettings } from "utils/salesInvoicePermissions";
import {
  isInvoiceSettingsForbiddenError,
  resolveOrderSaveErrorMessage,
} from "utils/orderInvoiceSettingsError";

function Section({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className={`${cardCls} space-y-4`}>
      {title && (
        <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
          {title}
        </h2>
      )}
      {children}
    </section>
  );
}

/** The orders list this page belongs to, and the way back to where the user came from. */
function useLeavePage() {
  const navigate = useNavigate();
  const { companyId, customerId } = useParams();
  const ordersPath = customerId
    ? `/app/${companyId}/customers/${customerId}/orders`
    : `/app/${companyId}/orders`;
  // A page opened by link has no "before".
  const goBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate(ordersPath);
  };
  return { ordersPath, goBack };
}

/**
 * Create (`.../orders/new`) or edit (`.../orders/:orderId/edit`) a contract. It
 * is a page rather than a dialog so that what has been typed survives a
 * mis-click, a reload or leaving to look something up — see use-order-draft.ts.
 * Under `customers/:customerId/...` the customer is fixed by the route.
 *
 * Keyed by the route: moving straight from one order's edit page to another's
 * (or to "new") must start a fresh form, never keep the previous order's
 * fields, which a save would otherwise write onto the next order.
 */
function OrderForm() {
  const { companyId, customerId, orderId } = useParams();
  return <OrderFormLoader key={`${companyId}:${customerId || "-"}:${orderId || "new"}`} />;
}

/** An order being edited is loaded first; the form then starts from it. */
function OrderFormLoader() {
  const { t } = useTranslation();
  const { companyId, orderId } = useParams();
  const { goBack } = useLeavePage();
  const { loadedOrder, isLoading, loadFailed } = useLoadedOrder(orderId, companyId);

  if (isLoading) {
    return <Loading />;
  }

  if (loadFailed) {
    return (
      <div className="mx-auto max-w-4xl space-y-4 p-4 sm:p-6">
        <Notice tone="critical">
          <p>{t("error_getting_data")}</p>
        </Notice>
        <button type="button" className={btnSecondaryCls} onClick={goBack}>
          {t("back")}
        </button>
      </div>
    );
  }

  return <OrderFormPage loadedOrder={loadedOrder} />;
}

function OrderFormPage({ loadedOrder }: { loadedOrder: OrderStart | null }) {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { companyId, customerId: routeCustomerId, orderId } = useParams();
  const { role } = useSelector((state: RootState) => state.authReducer);
  const { ordersPath, goBack } = useLeavePage();
  // Same tier the backend's `canManageSalesInvoices` policy requires to
  // change these fields (orders/post, orders/patch, orders/invoice-settings)
  // — see utils/salesInvoicePermissions.ts.
  const canManageInvoiceSettings = canManageSalesInvoiceSettings(role);

  const [start] = useState(() => loadedOrder || newOrderStart(role?.user || ""));
  const startCustomerId = start.customerId || routeCustomerId;

  const [date, setDate] = useState(start.date);
  const [description, setDescription] = useState(start.description);
  const [responsibleUser, setResponsibleUser] = useState(start.responsibleUser);
  const [customer, setCustomer] = useState<string[]>(
    startCustomerId ? [startCustomerId] : []
  );
  // Product lines are kept in three places. `orderItems` is what gets saved:
  // ProductsSelect reports it only while every line has a quantity and a price.
  // `productLines` is every line, unfinished ones too: what the draft keeps and
  // what validateForm checks. `initialOrderitems` only seeds the picker when it
  // is (re)mounted.
  const [orderItems, setOrderItems] = useState(start.lines);
  const [productLines, setProductLines] = useState(start.lines);
  const [initialOrderitems, setInitialOrderitems] = useState(start.lines);
  const [productGroups, setProductGroups] = useState(start.productGroups);
  const [initialProductGroups, setInitialProductGroups] = useState(
    start.productGroups
  );
  /** The two product pickers keep their lines inside; a new version remounts them with `initial…`. */
  const [pickersVersion, setPickersVersion] = useState(0);
  const [billing, setBilling] = useState(start.billing);
  const [termError, setTermError] = useState("");
  const [isExternal, setIsExternal] = useState(start.isExternal);
  const [externalNumber, setExternalNumber] = useState(start.externalNumber);
  const [externalNumberError, setExternalNumberError] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [saveBtnLoading, setSaveBtnLoading] = useState(false);
  // A closed order's invoice settings are frozen server-side: the fields are
  // disabled, and buildOrderPayload leaves them out so the rest can still save.
  const billingSettingsDisabled = !canManageInvoiceSettings || start.isClosed;

  const { foundDraft, restore, discard, clear } = useOrderDraft(
    orderDraftKey({ companyId: companyId || "", customerId: routeCustomerId, orderId }),
    {
      date,
      customer,
      responsibleUser,
      description,
      productLines,
      productGroups,
      billing,
      isExternal,
      externalNumber,
    }
  );

  const handleRestoreDraft = () => {
    const draft = restore();
    if (!draft) return;
    setDate(draft.date);
    setCustomer(draft.customer);
    setResponsibleUser(draft.responsibleUser);
    setDescription(draft.description);
    setProductLines(draft.productLines);
    setInitialOrderitems(draft.productLines);
    setProductGroups(draft.productGroups);
    setInitialProductGroups(draft.productGroups);
    setPickersVersion((version) => version + 1);
    setBilling(draft.billing);
    setIsExternal(draft.isExternal);
    setExternalNumber(draft.externalNumber);
  };

  const validateForm = (): boolean => {
    setErrMsg("");
    setExternalNumberError("");
    setTermError("");
    const fail = (message: string) => {
      setErrMsg(message);
      return false;
    };
    // `orderItems` leaves out unfinished lines, so on its own it would let
    // them be dropped silently (or, after a restore, an older list be saved).
    const unfinishedLine = productLines.find(
      (line) => !(line.quantity && line.price)
    );

    if (isExternal && !externalNumber.trim()) {
      setExternalNumberError(t("order_external_number_required"));
      return fail(t("order_external_number_required"));
    }
    if (!date || !responsibleUser) return fail(t("please_fill_required_fields"));
    if (customer.length === 0) return fail(t("customer_is_required"));
    if (unfinishedLine) {
      return fail(t("order_line_incomplete", { title: unfinishedLine.title }));
    }
    if (orderItems.length === 0 && productGroups.length === 0) {
      return fail(t("at_least_one_product_or_product_group_required"));
    }
    if (isScheduleDayInvalid(billing)) {
      return fail(t("order_invoice_schedule_day_invalid"));
    }
    if (billing.isRecurring && isContractTermIncomplete(billing.term)) {
      setTermError(t("contract_term_date_required"));
      return fail(t("contract_term_date_required"));
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const customerId = customer[0];
    const payload = buildOrderPayload({
      orderId,
      companyId,
      customerId,
      date,
      description,
      responsibleUser,
      orderItems,
      productGroups,
      billing,
      isClosed: start.isClosed,
      loadedContractEndDate: start.contractEndDate,
      isExternal,
      externalNumber,
    });
    const action = orderId ? patch : post;
    try {
      setSaveBtnLoading(true);
      const result = await action(payload);
      const savedOrderId = orderId || result.createdOrder.id;
      clear();
      if (companyId) {
        dispatch(fetchCustomerOrdersList(companyId, customerId));
        dispatch(fetchOrders({ filterValue: {}, companyId }));
        if (orderId) dispatch(fetchOrderDetails({ companyId, orderId }));
      }
      toast.success(t("saved_successfully"));
      // Replace, so that Back from the order does not return to this form.
      navigate(`${ordersPath}/${savedOrderId}`, { replace: true });
    } catch (e: any) {
      console.error(e);
      // The server names what it refused (a schedule day, a billing start, a
      // contract number); "error" alone left the user guessing which field.
      // The billing-settings permission 403 gets its own friendlier message
      // (utils/orderInvoiceSettingsError.ts); everything else, including the
      // external-contract codes, goes through the external-contract mapper.
      setErrMsg(
        isInvoiceSettingsForbiddenError(e)
          ? resolveOrderSaveErrorMessage(e, t)
          : externalContractErrorMessage(e, t)
      );
      setSaveBtnLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6">
      <header className="flex items-center gap-3">
        <button
          type="button"
          className={btnIconCls}
          onClick={goBack}
          aria-label={t("back")}
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
        </button>
        <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">
          {orderId ? t("order_form_title_edit") : t("create_order")}
        </h1>
      </header>

      {foundDraft && (
        <DraftBanner
          savedAt={foundDraft.savedAt}
          onRestore={handleRestoreDraft}
          onDiscard={discard}
        />
      )}

      <Section title={t("order_form_section_customer")}>
        <DatePicker
          disableDatesAfter={null}
          title={t("date")}
          isRequired={true}
          onChange={(value: Date | null) =>
            setDate(value ? vilniusYmd(value) : "")
          }
          selected={date}
        />
        {/* Polaris keeps the chosen customer's chip as wide as its text, even past the field. On a phone the
            chip may shrink to the field and wrap (a tooltip would not show on a touch screen); wider
            screens keep Polaris's single line with an ellipsis. */}
        <div className="max-md:[&_.Polaris-Connected\_\_Item]:min-w-0 max-md:[&_.Polaris-LegacyStack\_\_Item]:min-w-0 max-md:[&_.Polaris-LegacyStack\_\_Item]:max-w-full max-md:[&_.Polaris-Tag\_\_TagText]:whitespace-normal max-md:[&_.Polaris-Tag\_\_TagText]:[overflow-wrap:anywhere]">
          <CustomerSelect
            value={customer}
            onChange={setCustomer}
            isRequired={true}
            allowMultiple={false}
            readonly={!!routeCustomerId}
          />
        </div>
        {/* The responsible person goes on the order's documents and invoices. */}
        <UserSelect
          onChange={setResponsibleUser}
          value={responsibleUser}
          label={"responsible_user"}
          isRequired={true}
        />
      </Section>

      <Section title={t("order_form_section_items")}>
        <ProductsSelect
          key={`products-${pickersVersion}`}
          allowCreate
          onChange={(value: Product[]) => {
            if (JSON.stringify(orderItems) !== JSON.stringify(value)) {
              setOrderItems(value);
            }
          }}
          onLinesChange={setProductLines}
          initialProducts={initialOrderitems}
          isRequired={false}
        />
        <ProductGroupSelect
          key={`groups-${pickersVersion}`}
          onProductGroupsChange={setProductGroups}
          initialProductGroups={initialProductGroups}
          isRequired={false}
        />
        <div>
          <label htmlFor="order-description" className={labelCls}>
            {t("description")}
          </label>
          <input
            id="order-description"
            className={inputCls}
            autoComplete="off"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </Section>

      <Section>
        {companyId && (
          <ExternalContractFields
            companyId={companyId}
            orderId={orderId}
            isExternal={isExternal}
            onExternalChange={setIsExternal}
            externalNumber={externalNumber}
            onExternalNumberChange={(value) => {
              setExternalNumber(value);
              setExternalNumberError("");
            }}
            numberError={externalNumberError}
          />
        )}
      </Section>

      <Section title={t("order_form_section_invoices")}>
        <InvoicesSection
          billing={billing}
          onChange={(next) => {
            setBilling(next);
            setTermError("");
          }}
          disabled={billingSettingsDisabled}
          canManage={canManageInvoiceSettings}
          isClosed={start.isClosed}
          isExternal={isExternal}
          termError={termError}
          order={{ date, createdAt: start.createdAt, capPeriods: start.capPeriods }}
          loadedStartMonth={start.billing.startMonth}
        />
      </Section>

      {/* Sticks to the bottom of the scrolled page. The page already keeps the phone bar's height as
          bottom padding, and a sticky inset counts from inside that padding, so the inset stays 0:
          adding the bar height again floated this bar above the phone bar. z-20 keeps it over the fields. */}
      <div className="sticky bottom-0 z-20 -mx-4 space-y-2 border-t border-zinc-200 bg-white/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6 dark:border-zinc-700 dark:bg-zinc-900/95">
        {errMsg && (
          // errMsg is already a display-ready string — every setErrMsg call
          // site translates (or falls back to server/Axios text) before
          // storing it. Translating it again here is wrong: a translated
          // sentence is not a valid i18next key, and one containing ":" gets
          // misread as a namespace separator.
          <Notice tone="critical">{errMsg}</Notice>
        )}
        <div className="flex gap-3 max-md:flex-col-reverse md:justify-end">
          <button
            type="button"
            className={`${btnSecondaryCls} max-md:min-h-11`}
            onClick={goBack}
          >
            {t("cancel")}
          </button>
          <button
            type="button"
            className={`${btnPrimaryCls} max-md:min-h-11`}
            onClick={handleSave}
            disabled={saveBtnLoading}
          >
            {saveBtnLoading ? t("loading") : t("save")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default OrderForm;
